/* Degen Fits — global.js
   Vanilla, dependency-free, progressive enhancement only. Every feature here
   has a native fallback path: forms submit, links navigate, the site works
   without this file. Custom elements guard against double-definition so
   Theme Editor section reloads never re-register or leak listeners. */
(() => {
  'use strict';

  /* ---------- shared globals ---------- */
  const globalsEl = document.getElementById('theme-globals');
  const G = globalsEl ? JSON.parse(globalsEl.textContent) : { routes: {}, strings: {}, moneyFormat: '${{amount}}' };
  const t = (key, repl) => {
    let s = (G.strings && G.strings[key]) || '';
    if (repl) for (const [k, v] of Object.entries(repl)) s = s.replace(`[${k}]`, v);
    return s;
  };

  const announce = (message) => {
    const region = document.getElementById('A11yAnnounce');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => { region.textContent = message; });
  };

  const formatMoney = (cents) => {
    const value = (typeof cents === 'string') ? parseFloat(cents) : cents;
    /* money_format may contain HTML (e.g. <span class=money>); output goes into
       textContent, so strip tags from the template. */
    const format = (G.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const withDelims = (num, precision, thousands, decimal) => {
      const fixed = (num / 100).toFixed(precision);
      const [whole, dec] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? grouped + decimal + dec : grouped;
    };
    let amount;
    if (format.includes('amount_no_decimals_with_comma_separator')) amount = withDelims(value, 0, '.', ',');
    else if (format.includes('amount_with_comma_separator')) amount = withDelims(value, 2, '.', ',');
    else if (format.includes('amount_no_decimals')) amount = withDelims(value, 0, ',', '.');
    else amount = withDelims(value, 2, ',', '.');
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  const debounce = (fn, wait) => {
    let handle;
    return (...args) => {
      clearTimeout(handle);
      handle = setTimeout(() => fn(...args), wait);
    };
  };

  window.DegenFits = { announce, formatMoney, t, globals: G };

  /* ---------- color mode ---------- */
  const MODE_KEY = 'degen-fits:color-mode';

  const applyMode = (mode) => {
    const root = document.documentElement;
    root.setAttribute('data-color-mode', mode);
    root.style.colorScheme = mode;
    const meta = document.getElementById('MetaThemeColor');
    if (meta) meta.setAttribute('content', mode === 'light' ? '#ffffff' : '#0b0b0a');
    document.querySelectorAll('theme-toggle button').forEach((btn) => btn.setAttribute('aria-label', mode === 'dark' ? t('switchToLight') : t('switchToDark')));
  };

  if (!customElements.get('theme-toggle')) {
    customElements.define('theme-toggle', class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;
        const mode = document.documentElement.getAttribute('data-color-mode') || 'light';
        this.button.setAttribute('aria-label', mode === 'dark' ? t('switchToLight') : t('switchToDark'));
        this.onClick = () => {
          const next = document.documentElement.getAttribute('data-color-mode') === 'dark' ? 'light' : 'dark';
          applyMode(next);
          try { window.localStorage.setItem(MODE_KEY, next); } catch (error) { /* blocked storage: session-only */ }
          announce(next === 'dark' ? t('darkOn') : t('lightOn'));
        };
        this.button.addEventListener('click', this.onClick);
      }
      disconnectedCallback() {
        if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      }
    });
  }

  /* ---------- dialogs & drawers (native <dialog>) ---------- */
  const scrollLock = {
    count: 0,
    add() { this.count += 1; document.documentElement.classList.add('overlay-open'); },
    remove() {
      this.count = Math.max(0, this.count - 1);
      if (this.count === 0) document.documentElement.classList.remove('overlay-open');
    },
  };

  if (!customElements.get('drawer-dialog')) {
    customElements.define('drawer-dialog', class extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;
        this.releaseLock = () => {
          if (this.lockHeld) {
            this.lockHeld = false;
            scrollLock.remove();
          }
        };
        this.onClose = () => {
          this.releaseLock();
          if (this.invoker && document.contains(this.invoker) && (document.activeElement === document.body || document.activeElement === null)) {
            this.invoker.focus();
          }
          this.invoker = null;
        };
        this.onClick = (event) => {
          if (event.target.closest('[data-drawer-close]')) this.close();
          /* click on backdrop (the dialog element itself, outside its children) */
          if (event.target === this.dialog) {
            const rect = this.dialog.getBoundingClientRect();
            const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
            if (!inside) this.close();
          }
        };
        this.dialog.addEventListener('close', this.onClose);
        this.dialog.addEventListener('click', this.onClick);
      }
      disconnectedCallback() {
        if (!this.dialog) return;
        this.dialog.removeEventListener('close', this.onClose);
        this.dialog.removeEventListener('click', this.onClick);
        if (this.dialog.open) { this.dialog.close(); }
        this.releaseLock();
      }
      open(invoker) {
        if (!this.dialog || this.dialog.open) return;
        this.invoker = invoker || document.activeElement;
        this.dialog.showModal();
        this.lockHeld = true;
        scrollLock.add();
        const target = this.dialog.querySelector('[data-autofocus]') || this.dialog.querySelector('button, [href], input, select, textarea');
        if (target) target.focus();
      }
      close() {
        if (this.dialog && this.dialog.open) {
          this.dialog.close();
          this.releaseLock(); /* synchronous: the close event also calls this, idempotently */
        }
      }
    });
  }

  document.addEventListener('click', (event) => {
    /* Close any open nav dropdown when clicking elsewhere. */
    document.querySelectorAll('details.nav__dropdown[open]').forEach((dropdown) => {
      if (!dropdown.contains(event.target)) dropdown.removeAttribute('open');
    });
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const host = document.getElementById(opener.getAttribute('data-drawer-open'));
    if (host && typeof host.open === 'function') {
      event.preventDefault();
      host.open(opener);
    }
  });

  /* ---------- cart ---------- */
  const dispatchCart = (cart) => {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  document.addEventListener('cart:updated', (event) => {
    const count = event.detail && event.detail.cart ? event.detail.cart.item_count : null;
    if (count === null || count === undefined) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
      el.toggleAttribute('hidden', count === 0);
    });
    document.querySelectorAll('[data-cart-count-a11y]').forEach((el) => {
      const template = (count === 1 && el.getAttribute('data-cart-count-a11y-one')) || el.getAttribute('data-cart-count-a11y');
      el.textContent = template.replace('[count]', count);
    });
  });

  const Cart = {
    /* FIFO queue: cart mutations resolve in order so a slow early response can
       never repaint the drawer/badge with stale state after a fast later one. */
    queue: Promise.resolve(),
    enqueue(job) {
      const run = this.queue.then(job, job);
      this.queue = run.catch(() => {});
      return run;
    },
    sectionsFor() {
      return document.querySelector('cart-drawer') ? 'cart-drawer' : null;
    },
    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let data = null;
      try { data = await response.json(); } catch (error) { /* non-JSON (challenge/HTML) */ }
      if (!response.ok || data === null) {
        const message = data && (data.description || data.message);
        throw new Error(typeof message === 'string' ? message : t('cartError'));
      }
      return data;
    },
    add(items) {
      return this.enqueue(async () => {
        const sections = this.sectionsFor();
        const payload = { items };
        if (sections) payload.sections = sections;
        const data = await this.request(G.routes.cartAdd, payload);
        const cart = await this.refreshState(data.sections);
        return cart;
      });
    },
    change(line, quantity) {
      return this.enqueue(async () => {
        const sections = this.sectionsFor();
        const payload = { line, quantity };
        if (sections) payload.sections = sections;
        const data = await this.request(G.routes.cartChange, payload);
        if (data.sections) this.injectDrawer(data.sections);
        dispatchCart(data);
        return data;
      });
    },
    async refreshState(sections) {
      if (sections) this.injectDrawer(sections);
      const response = await fetch(`${G.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      dispatchCart(cart);
      return cart;
    },
    injectDrawer(sections) {
      const html = sections && sections['cart-drawer'];
      const host = document.querySelector('cart-drawer');
      if (!html || !host) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-drawer-content]');
      const current = host.querySelector('[data-drawer-content]');
      if (fresh && current) {
        const hadFocus = current.contains(document.activeElement);
        current.replaceWith(fresh);
        if (hadFocus) {
          const dialog = host.querySelector('dialog');
          if (dialog && dialog.open) {
            const target = dialog.querySelector('[data-autofocus]') || dialog.querySelector('button, [href], input');
            if (target) target.focus();
          }
        }
      }
    },
  };
  window.DegenFits.cart = Cart;

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return; /* re-attach safe: listeners bind once */
        this.initialized = true;
        this.addEventListener('change', (event) => {
          const input = event.target.closest('[data-line-quantity]');
          if (input) this.updateLine(input, parseInt(input.value, 10));
        });
        this.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-line-remove]');
          if (remove) {
            event.preventDefault();
            this.updateLine(remove, 0);
          }
        });
      }
      get host() { return this.querySelector('drawer-dialog'); }
      open(invoker) { if (this.host) this.host.open(invoker); }
      async updateLine(el, quantity) {
        const line = parseInt(el.getAttribute('data-line') || el.closest('[data-line]')?.getAttribute('data-line'), 10);
        if (!line || Number.isNaN(quantity)) return;
        const lineEl = el.closest('.cart-line');
        if (lineEl) lineEl.setAttribute('aria-busy', 'true');
        try {
          const cart = await Cart.change(line, quantity);
          announce(t('cartUpdated', { count: cart.item_count }));
        } catch (error) {
          announce(error.message || t('cartError'));
          const errEl = this.querySelector('[data-cart-error]');
          if (errEl) { errEl.textContent = error.message || t('cartError'); errEl.hidden = false; }
        } finally {
          /* success replaces the line via injectDrawer; on failure clear busy */
          if (lineEl && lineEl.isConnected) lineEl.removeAttribute('aria-busy');
        }
      }
    });
  }

  /* Cart page enhancement: quantity/remove without full submit (falls back to native form + links). */
  if (!customElements.get('cart-page-form')) {
    customElements.define('cart-page-form', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        /* With JS enabled, quantity edits update via AJAX on change; intercept
           implicit submissions (Enter in a quantity field) so they can never
           fall through to the checkout submit button. Explicit checkout
           clicks pass through natively. */
        this.addEventListener('submit', (event) => {
          const submitter = event.submitter;
          if (!submitter || submitter.name !== 'checkout') event.preventDefault();
        });
        this.addEventListener('change', async (event) => {
          const input = event.target.closest('[data-line-quantity]');
          if (!input) return;
          event.preventDefault();
          await this.change(input, parseInt(input.value, 10));
        });
        this.addEventListener('click', async (event) => {
          const remove = event.target.closest('[data-line-remove]');
          if (!remove) return;
          event.preventDefault();
          await this.change(remove, 0);
        });
      }
      async change(el, quantity) {
        const line = parseInt(el.getAttribute('data-line') || el.closest('[data-line]')?.getAttribute('data-line'), 10);
        if (!line || Number.isNaN(quantity)) return;
        try {
          await Cart.change(line, quantity);
          window.location.reload();
        } catch (error) {
          const errEl = this.querySelector('[data-cart-error]');
          if (errEl) { errEl.textContent = error.message || t('cartError'); errEl.hidden = false; }
          announce(error.message || t('cartError'));
        }
      }
    });
  }

  /* ---------- product form (AJAX add-to-cart with native fallback) ---------- */
  if (!customElements.get('product-form')) {
    customElements.define('product-form', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.submitButton = this.form.querySelector('[data-submit]');
        this.errorEl = this.querySelector('[data-form-error]');
        this.onSubmit = (event) => this.handleSubmit(event);
        this.form.addEventListener('submit', this.onSubmit);
      }
      disconnectedCallback() { if (this.form) this.form.removeEventListener('submit', this.onSubmit); }
      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }
      async handleSubmit(event) {
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') {
          /* sold-out/unavailable state: communicated on the button; block the POST */
          event.preventDefault();
          this.setError(t('soldOut'));
          return;
        }
        const idInput = this.form.querySelector('input[name="id"], select[name="id"]');
        const picker = document.getElementById(this.getAttribute('data-picker') || '');
        if (picker && typeof picker.reportMissing === 'function' && picker.reportMissing()) {
          event.preventDefault();
          return;
        }
        if (!idInput || idInput.disabled || !idInput.value) {
          event.preventDefault();
          this.setError(t('optionsMissing', { names: (picker && picker.missingNames && picker.missingNames()) || '' }));
          return;
        }
        event.preventDefault();
        this.setError('');
        const quantityInput = this.form.querySelector('input[name="quantity"]');
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
        const originalLabel = this.submitButton ? this.submitButton.textContent : '';
        if (this.submitButton) {
          this.submitButton.setAttribute('aria-busy', 'true');
          this.submitButton.textContent = t('adding');
        }
        try {
          await Cart.add([{ id: parseInt(idInput.value, 10), quantity }]);
          announce(t('added'));
          const drawer = document.querySelector('cart-drawer');
          if (drawer && document.body.getAttribute('data-cart-type') === 'drawer') drawer.open(this.submitButton);
          else window.location.assign(G.routes.cart);
        } catch (error) {
          this.setError(error.message || t('cartError'));
          announce(error.message || t('cartError'));
        } finally {
          if (this.submitButton) {
            this.submitButton.removeAttribute('aria-busy');
            this.submitButton.textContent = originalLabel;
          }
        }
      }
    });
  }

  /* ---------- quantity input ---------- */
  if (!customElements.get('qty-input')) {
    customElements.define('qty-input', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        if (this.initialized) return;
        this.initialized = true;
        this.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-qty-change]');
          if (!btn || !this.input) return;
          event.preventDefault();
          const step = parseInt(btn.getAttribute('data-qty-change'), 10);
          const min = parseInt(this.input.min || '1', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
          if (next !== parseInt(this.input.value, 10)) {
            this.input.value = String(next);
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });
  }

  /* ---------- announcement bar rotator ---------- */
  /* Progressive: the section server-renders message 1 with .is-active, so the
     bar is complete and correct before (or without) this file; the element
     only adds the timed swap. Motion is class-driven (is-active/is-leaving)
     and lives in CSS — the global prefers-reduced-motion block zeroes the
     transition durations, so any swap that does happen is instant for
     reduced-motion users. Additionally (design-audit round), auto-rotation
     itself never starts while (prefers-reduced-motion: reduce) matches —
     start() checks the media query and a dynamic flip to reduce stops the
     timer — while the public rotate() stays callable and synchronous (the
     QA asserts drive it directly). All rotation state lives on the element
     instance, so Theme Editor section re-renders can never leak a timer or
     restart it on unrelated renders. */
  if (!customElements.get('announcement-rotator')) {
    customElements.define('announcement-rotator', class extends HTMLElement {
      connectedCallback() {
        this.messages = Array.from(this.querySelectorAll('[data-announcement-msg]'));
        if (this.messages.length < 2) return; /* single message: static bar, no timer */
        if (this.initialized) {
          /* Re-attach (Theme Editor reload): the document/mediaQuery-level
             listeners were removed on disconnect and must come back — same
             function refs, so re-adding can never double-register (round-3
             audit precedent). Element-level listeners persist across detach;
             only the timer needs restarting. */
          document.addEventListener('visibilitychange', this.onVisibility);
          this.motionQuery.addEventListener('change', this.onMotionChange);
          this.start();
          return;
        }
        this.initialized = true;
        const interval = parseInt(this.getAttribute('data-interval'), 10);
        this.intervalMs = Number.isNaN(interval) ? 5000 : Math.max(3000, interval);
        this.index = Math.max(0, this.messages.findIndex((el) => el.classList.contains('is-active')));
        this.timer = null;
        this.leavingTimer = null;
        this.pointerOver = false;
        this.focusWithin = false;
        this.onVisibility = () => { if (document.hidden) this.stop(); else this.start(); };
        /* Reduced motion: no auto-rotation. start() re-checks the query on
           every arm attempt; a live flip to reduce stops the running timer
           (flipping back resumes only if no other pause condition holds). */
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onMotionChange = () => { if (this.motionQuery.matches) this.stop(); else this.start(); };
        /* Hover/focus pause: a linked message must stay readable and
           clickable under the pointer, and rotation may never yank a focused
           link out of the tab order (inactive messages are visibility:
           hidden). Resume only when both pointer and focus have left. */
        this.onPointerEnter = () => { this.pointerOver = true; this.stop(); };
        this.onPointerLeave = () => { this.pointerOver = false; this.start(); };
        this.onFocusIn = () => { this.focusWithin = true; this.stop(); };
        this.onFocusOut = (event) => {
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.focusWithin = false;
          this.start();
        };
        this.onTransitionEnd = (event) => {
          if (event.target.classList.contains('is-leaving')) this.clearLeaving();
        };
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.addEventListener('transitionend', this.onTransitionEnd);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.motionQuery.addEventListener('change', this.onMotionChange);
        this.start();
      }
      disconnectedCallback() {
        this.stop();
        this.clearLeaving();
        document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.motionQuery) this.motionQuery.removeEventListener('change', this.onMotionChange);
      }
      start() {
        /* init guard + every pause condition (incl. reduced motion); never
           double-arms the timer */
        if (!this.initialized || this.timer || document.hidden || this.pointerOver || this.focusWithin || this.motionQuery.matches) return;
        this.timer = setInterval(() => this.rotate(), this.intervalMs);
      }
      stop() {
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
      }
      clearLeaving() {
        if (this.leavingTimer) { clearTimeout(this.leavingTimer); this.leavingTimer = null; }
        this.messages.forEach((el) => el.classList.remove('is-leaving'));
      }
      /* Public: the QA harness drives this synchronously (timers/rAF cannot
         be relied on under headless --dump-dom); the interval only ever calls
         this same method. Each call advances one step: active -> leaving
         (cleared on transitionend, by the timeout below, or synchronously by
         the next rotate), next -> active. */
      rotate() {
        if (!this.initialized) return;
        this.clearLeaving(); /* a still-pending previous exit resolves now, so state stays clean even when no timer or transitionend ever fires */
        const current = this.messages[this.index];
        this.index = (this.index + 1) % this.messages.length;
        const next = this.messages[this.index];
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        next.classList.add('is-active');
        /* Fallback for transitionend never firing (backgrounded tab,
           interrupted transition): motion is 400ms, so 500ms covers it. */
        this.leavingTimer = setTimeout(() => this.clearLeaving(), 500);
      }
    });
  }

  /* ---------- predictive search (combobox pattern) ----------
     Dormant since round 3: the header search panel became a link to the
     dedicated search page, so nothing currently instantiates this element.
     Kept (with sections/predictive-search.liquid) as the supported path for
     re-enabling live suggestions later. */
  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.panel = this.querySelector('[data-predictive-panel]');
        if (!this.input || !this.panel) return;
        if (this.initialized) { document.addEventListener('click', this.onDocClick); return; }
        this.initialized = true;
        this.abortController = null;
        this.activeIndex = -1;
        this.onInput = debounce(() => this.query(), 250);
        this.onKeydown = (event) => this.handleKeys(event);
        this.onDocClick = (event) => { if (!this.contains(event.target)) this.closePanel(); };
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocClick);
      }
      disconnectedCallback() { document.removeEventListener('click', this.onDocClick); }
      options() { return Array.from(this.panel.querySelectorAll('[role="option"]')); }
      openPanel() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
      closePanel() {
        this.panel.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.activeIndex = -1;
      }
      setStatus(message) {
        /* textContent only: `message` can embed user-typed search terms. */
        this.openPanel();
        this.panel.textContent = '';
        const status = document.createElement('p');
        status.className = 'predictive-results__status';
        status.setAttribute('role', 'status');
        status.textContent = message;
        this.panel.appendChild(status);
      }
      async query() {
        const terms = this.input.value.trim();
        if (this.abortController) this.abortController.abort();
        if (terms.length < 2) { this.closePanel(); return; }
        this.abortController = new AbortController();
        this.setStatus(t('loading'));
        try {
          const url = `${G.routes.predictiveSearch}?q=${encodeURIComponent(terms)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error('http');
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const results = doc.querySelector('[data-predictive-results]');
          if (results && results.querySelector('[role="option"]')) {
            this.panel.innerHTML = '';
            this.panel.appendChild(results);
            this.openPanel();
            this.activeIndex = -1;
          } else {
            this.setStatus(t('noSuggestions', { terms }));
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.setStatus(t('searchError'));
        }
      }
      handleKeys(event) {
        if (event.key === 'Escape') {
          if (!this.panel.hidden) { event.preventDefault(); this.closePanel(); }
          return;
        }
        if (this.panel.hidden) return;
        const options = this.options();
        if (!options.length) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const delta = event.key === 'ArrowDown' ? 1 : -1;
          this.activeIndex = (this.activeIndex + delta + options.length) % options.length;
          options.forEach((opt, index) => opt.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false'));
          const active = options[this.activeIndex];
          this.input.setAttribute('aria-activedescendant', active.id);
          active.scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && this.activeIndex >= 0) {
          event.preventDefault();
          window.location.assign(options[this.activeIndex].getAttribute('href'));
        } else if (event.key === 'Tab') {
          this.closePanel();
        }
      }
    });
  }

  /* ---------- facets: auto-apply convenience (native GET submit) ---------- */
  if (!customElements.get('facets-form')) {
    customElements.define('facets-form', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form') || this.closest('form') || document.getElementById(this.getAttribute('data-form') || '');
        if (this.initialized) return;
        this.initialized = true;
        this.addEventListener('change', (event) => {
          if (!this.form) return;
          const control = event.target;
          /* Auto-apply only outside the filter drawer (e.g. the sort select).
             Inside the drawer, users multi-select and press Apply — a full
             navigation per checkbox would close the drawer each time. */
          if (control.closest('dialog')) return;
          if (control.matches('input[type="checkbox"], select')) this.form.requestSubmit();
        });
      }
    });
  }

  /* ---------- Escape closes nav dropdowns ---------- */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const dropdown = document.querySelector('details.nav__dropdown[open]');
    if (dropdown && dropdown.contains(document.activeElement)) {
      dropdown.removeAttribute('open');
      const dropdownSummary = dropdown.querySelector('summary');
      if (dropdownSummary) dropdownSummary.focus();
    }
  });

  /* ---------- sticky header scrolled state ---------- */
  /* Paint-only state (translucent surface + shadow) — never layout. The
     lookup runs per frame so Theme Editor section reloads need no rebind. */
  const updateHeaderScrolled = () => {
    const wrap = document.querySelector('.header-wrap:not(.header-wrap--static)');
    if (wrap) wrap.toggleAttribute('data-scrolled', window.scrollY > 4);
  };
  window.DegenFits.updateHeaderScrolled = updateHeaderScrolled;
  let headerScrollTick = false;
  updateHeaderScrolled();
  window.addEventListener('scroll', () => {
    if (headerScrollTick) return;
    headerScrollTick = true;
    window.requestAnimationFrame(() => {
      headerScrollTick = false;
      updateHeaderScrolled();
    });
  }, { passive: true });

  /* ---------- forms: focus server-returned status messages ---------- */
  /* [data-form-status] only renders after a server round-trip (success or
     errors), so moving focus there on load is always post-submit context. */
  const status = document.querySelector('[data-form-status]');
  if (status) {
    status.setAttribute('tabindex', '-1');
    status.focus({ preventScroll: false });
  }

  /* ---------- confirmed destructive submits (progressive: no-JS submits natively) ---------- */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-confirm]');
    if (form && !window.confirm(form.getAttribute('data-confirm'))) event.preventDefault();
  });

  /* ---------- address forms: restore the saved country selection ---------- */
  document.querySelectorAll('select[data-default]').forEach((select) => {
    const saved = select.getAttribute('data-default');
    if (!saved) return;
    select.value = saved;
    if (select.selectedIndex === -1) {
      Array.from(select.options).forEach((option) => {
        if (option.text === saved) select.value = option.value;
      });
    }
  });
})();
